import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

// Fallback defaults so the site can render before the first fetch.
const FALLBACK = {
  company_name: "Handy Meister",
  legal_name: "Handy Meister GmbH",
  phone: "+49 30 12345678",
  whatsapp: "+49 170 1234567",
  email: "service@handymeister.de",
  street: "Alexanderplatz 4",
  postal_code: "10178",
  city: "Berlin",
  country: "Deutschland",
  tax_id: "",
  register_number: "",
  opening_hours: {
    mo: { closed: false, open_time: "09:00", close_time: "19:00", note: "" },
    tu: { closed: false, open_time: "09:00", close_time: "19:00", note: "" },
    we: { closed: false, open_time: "09:00", close_time: "19:00", note: "" },
    th: { closed: false, open_time: "09:00", close_time: "19:00", note: "" },
    fr: { closed: false, open_time: "09:00", close_time: "19:00", note: "" },
    sa: { closed: false, open_time: "10:00", close_time: "16:00", note: "" },
    su: { closed: true, open_time: "", close_time: "", note: "" },
  },
  special_notes: "",
};

// Session-scoped shared value + subscriber list so every mounted hook stays in
// sync when any component triggers a refetch. No stale module cache — we
// always refetch on: mount, route change, window focus, and the custom
// "handymeister:data-updated" event emitted by admin save handlers.
let sharedCompany = null;
const subscribers = new Set();
let currentPromise = null;

function fetchAndBroadcast() {
  if (currentPromise) return currentPromise;
  currentPromise = api.get("/company")
    .then((r) => {
      sharedCompany = { ...FALLBACK, ...(r.data || {}) };
      subscribers.forEach((cb) => cb(sharedCompany));
      return sharedCompany;
    })
    .catch(() => sharedCompany || FALLBACK)
    .finally(() => { currentPromise = null; });
  return currentPromise;
}

export function useCompany() {
  const [company, setCompany] = useState(sharedCompany || FALLBACK);
  const { pathname } = useLocation();

  useEffect(() => {
    let mounted = true;
    const cb = (c) => { if (mounted) setCompany(c); };
    subscribers.add(cb);
    fetchAndBroadcast();
    return () => { mounted = false; subscribers.delete(cb); };
  }, [pathname]);

  useEffect(() => {
    const refresh = () => fetchAndBroadcast();
    const onStorage = (e) => { if (e.key === "handymeister:data-updated") fetchAndBroadcast(); };
    window.addEventListener("focus", refresh);
    window.addEventListener("handymeister:data-updated", refresh);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("handymeister:data-updated", refresh);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return company;
}

// Utility helpers used across the site.
export const digitsOnly = (s) => (s || "").replace(/[^0-9]/g, "");
export const toTelHref = (num) => `tel:${(num || "").replace(/\s+/g, "")}`;
export const toWhatsAppHref = (num) => `https://wa.me/${digitsOnly(num)}`;
export const toMapsHref = (company) =>
  `https://maps.google.com/?q=${encodeURIComponent(
    [company?.street, company?.postal_code + " " + company?.city, company?.country].filter(Boolean).join(", ")
  )}`;
export const fullAddress = (c) =>
  [c?.street, `${c?.postal_code || ""} ${c?.city || ""}`.trim()].filter(Boolean).join(", ");

export function formatHours(day) {
  if (!day) return "—";
  if (day.closed) return "Geschlossen";
  const hrs = `${day.open_time || ""}–${day.close_time || ""}`;
  return day.note ? `${hrs} (${day.note})` : hrs;
}

export const DAY_LABELS = {
  mo: "Montag", tu: "Dienstag", we: "Mittwoch", th: "Donnerstag",
  fr: "Freitag", sa: "Samstag", su: "Sonntag",
};
export const DAY_LABELS_SHORT = { mo: "Mo", tu: "Di", we: "Mi", th: "Do", fr: "Fr", sa: "Sa", su: "So" };
export const DAY_KEYS = ["mo", "tu", "we", "th", "fr", "sa", "su"];

/** Notify all live-listening hooks (public site + admin) that data changed.
 * Call this from Admin save handlers so open tabs refresh instantly. */
export function notifyDataUpdated() {
  window.dispatchEvent(new CustomEvent("handymeister:data-updated"));
  // Also cross-tab via localStorage
  try { localStorage.setItem("handymeister:data-updated", String(Date.now())); } catch (_) {}
}
